import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Card, Flex, Text } from 'core/components';
import { Field, Select } from 'core/form';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import storeLoader, { InjectedStoreProps } from 'app/stores/storeLoader';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import UdeFilterItem from './UdeFilterItem';
import UdeMetricFilterItem from './UdeMetricFilterItem';
import { FilterGroupState } from './filter.types';

interface AppStore {
  darkThemeEnabled: boolean;
}

interface UdeFilterGroupProps extends InjectedStoreProps<['$app']> {
  group: FilterGroupState;
  groups: {
    remove: (index: number) => void;
  };
  index: number;
  measurement: string | null;
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  disableRemoveGroup?: boolean;
  /** Field name path - used to detect metric vs dimension mode */
  fieldName?: string;
  $app: AppStore;
}

/**
 * Check if this filter group is for metric filters based on fieldName
 */
function isMetricFilterMode(fieldName?: string): boolean {
  return fieldName?.includes('metrics') ?? false;
}

const UdeFilterGroup = observer(
  ({
    group,
    groups,
    index,
    measurement,
    measurements,
    measurementHash,
    disableRemoveGroup = false,
    fieldName,
    $app
  }: UdeFilterGroupProps) => {
    const isMetricMode = useMemo(() => isMetricFilterMode(fieldName), [fieldName]);

    const handleAddFilter = useCallback(() => {
      const { filters } = group;
      const existingFilters = filters.fieldStates;
      const lastFilter = existingFilters.length > 0 ? existingFilters[existingFilters.length - 1] : null;

      if (isMetricMode) {
        // Metric filter - include metric and aggregate fields
        filters.add({
          metric: '',
          aggregate: '',
          filterField: '',
          operator: '>',
          filterValue: ''
        });
      } else {
        // Dimension filter - use same filterField to speed up adding multiple conditions
        filters.add({
          filterField: lastFilter?.filterField?.getValue() || '',
          operator: lastFilter?.operator?.getValue() || '=',
          filterValue: ''
        });
      }
    }, [group, isMetricMode]);

    const handleRemoveFilter = useCallback(
      (filterIndex: number) => {
        group.filters.remove(filterIndex);
      },
      [group]
    );

    const handleRemoveGroup = useCallback(
      (e?: React.MouseEvent) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        groups.remove(index);
      },
      [groups, index]
    );

    const hasFilters = group.filters.fieldStates.length > 0;

    return (
      <Card bg={$app.darkThemeEnabled ? 'transparent' : undefined} borderRadius={2} mb={2}>
        {/* Header with connector controls */}
        <Flex
          bg={$app.darkThemeEnabled ? 'black.2' : 'rgba(16,22,26,0.05)'}
          p="4px"
          borderBottom="thin"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap={1} flex={1}>
            <Field field={group.not} showLabel={false} small mb={0}>
              <Select
                small
                minimal
                intent={group.not.getValue() ? 'danger' : 'success'}
                buttonStyle={{
                  fontWeight: 'medium'
                }}
                width="auto"
                menuWidth={100}
              />
            </Field>
            <Text small muted>
              results matching
            </Text>
            <Field field={group.connector} showLabel={false} mb={0} small>
              <Select small minimal buttonStyle={{ fontWeight: 'medium' }} width="auto" menuWidth={100} />
            </Field>
            <Text small muted>
              of the following:
            </Text>
          </Flex>

          {!disableRemoveGroup && (
            <Button text="Remove Group" onClick={handleRemoveGroup} intent="danger" minimal small />
          )}
        </Flex>

        {/* Filter items */}
        <Flex flexDirection="column">
          {group.filters.map((filter, idx, fields) =>
            isMetricMode ? (
              <UdeMetricFilterItem
                key={filter.filterField._id}
                filter={filter as unknown as Parameters<typeof UdeMetricFilterItem>[0]['filter']}
                index={idx}
                autoFocus={idx === fields.length - 1}
                onRemove={handleRemoveFilter}
                measurement={measurement}
                measurements={measurements}
                groupFilters={group.filters}
                groupConnector={group.connector}
              />
            ) : (
              <UdeFilterItem
                key={filter.filterField._id}
                filter={filter}
                index={idx}
                autoFocus={idx === fields.length - 1}
                onRemove={handleRemoveFilter}
                measurement={measurement}
                measurements={measurements}
                measurementHash={measurementHash}
              />
            )
          )}

          {!hasFilters && (
            <Box p={2}>
              <Text muted small>
                No filters in this group. Add a filter below.
              </Text>
            </Box>
          )}

          {/* Add filter button */}
          <Flex p={1}>
            <Button icon="plus" onClick={handleAddFilter} small minimal>
              Add Filter
            </Button>
          </Flex>
        </Flex>
      </Card>
    );
  }
);

export default storeLoader('$app')(UdeFilterGroup);
